import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTable } from '@mylighthouse/prism-ember';
import BillingAdminsCellsTdActions from 'frontend-payments/components/billing-admins/cells/td-actions';
import type { BillingAdmin } from 'frontend-payments/models';

interface BillingAdminsTableBillingAdminsSignature {
  Args: {
    billingAdmins: BillingAdmin[];
    isBillingAdminsLoading: boolean;
    onEditUser: (billingAdmin: BillingAdmin) => void;
    onRemoveUser: (billingAdmin: BillingAdmin) => void;
  };
}

const COLUMNS = {
  FIRST_NAME: 'firstName',
  LAST_NAME: 'lastName',
  EMAIL: 'email',
  ACTIONS: 'actions',
} as const;

export default class BillingAdminsTableBillingAdmins extends Component<BillingAdminsTableBillingAdminsSignature> {
  @service declare intl: Services['intl'];
  @service declare session: Services['session'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get authenticatedUserEmail() {
    return this.session.user?.email;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    return [
      {
        name: this.intl.t('payments.billing_admins.table.header.first_name'),
        valuePath: COLUMNS.FIRST_NAME,
        textAlign: 'left' as const,
        maxWidth: 150,
      },
      {
        name: this.intl.t('payments.billing_admins.table.header.last_name'),
        valuePath: COLUMNS.LAST_NAME,
        textAlign: 'left' as const,
        maxWidth: 150,
      },
      {
        name: this.intl.t('payments.billing_admins.table.header.email'),
        valuePath: COLUMNS.EMAIL,
        textAlign: 'left' as const,
      },
      {
        isSortable: false,
        name: '',
        valuePath: COLUMNS.ACTIONS,
        textAlign: 'right' as const,
        width: 24,
        tdComponent: BillingAdminsCellsTdActions,
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        onEditUser: (billingAdmin: BillingAdmin) =>
          this.onEditUser(billingAdmin),
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        onRemoveUser: (billingAdmin: BillingAdmin) => {
          this.onRemoveUser(billingAdmin);
        },
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    return this.args.billingAdmins.map((admin) => {
      return {
        [COLUMNS.FIRST_NAME]: admin.firstName,
        [COLUMNS.LAST_NAME]: admin.lastName,
        [COLUMNS.EMAIL]: admin.email,
        [COLUMNS.ACTIONS]: {
          billingAdmin: admin,
          isStaffUser: this.session.isStaffUser,
          showRemoveButton:
            this.args.billingAdmins.length > 1 &&
            admin.email !== this.authenticatedUserEmail,
          showEditButton: admin.email === this.authenticatedUserEmail,
        },
      };
    });
  }

  @action
  onEditUser(billingAdmin: BillingAdmin): void {
    this.args.onEditUser(billingAdmin);
  }

  @action
  onRemoveUser(billingAdmin: BillingAdmin): void {
    this.args.onRemoveUser(billingAdmin);
  }

  <template>
    <PrismTable
      @columns={{this.columns}}
      @enableAutoSortsUpdate={{true}}
      @highlightTitleColumn={{false}}
      @isLoading={{@isBillingAdminsLoading}}
      @nrOfEmptyRows={{2}}
      @nrOfLoadingRows={{3}}
      @rows={{this.rows}}
      @widthConstraint="eq-container"
      data-test-billing-admins-table
    />
  </template>
}
